import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Paper, Typography, Button, Alert, CircularProgress, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  LinearProgress, IconButton, Tooltip
} from '@mui/material';
import RttyTerminal from '../components/RttyTerminal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import { api } from '../services/api';

const RttyPage = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [rttySession, setRttySession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  
  // File transfer state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deviceFilePath, setDeviceFilePath] = useState('');
  const [downloadPath, setDownloadPath] = useState('');
  const [transferProgress, setTransferProgress] = useState(0);
  const [transferring, setTransferring] = useState(false);
  const [transferStatus, setTransferStatus] = useState('');
  const [suppressTerminal, setSuppressTerminal] = useState(false);
  const fileInputRef = useRef(null);
  const downloadBufferRef = useRef([]);
  const sendCommandRef = useRef(null);
  const downloadFilenameRef = useRef(null);
  const requestedDownloadPathRef = useRef(null);
  const terminalRef = useRef(null);
  const transferringRef = useRef(false);
  const downloadTotalSizeRef = useRef(0); // Track total file size for progress calculation
  const uploadCanceledRef = useRef(false); // Track if upload was canceled to stop chunk sending

  // Initialize RTTY session
  useEffect(() => {
    const initializeSession = async () => {
      try {
        console.log('Initializing RTTY session for device:', serial);
        const response = await api.startRttySession(serial);
        console.log('RTTY session initialized:', response);
        setRttySession(response);
      } catch (error) {
        console.error('Failed to initialize RTTY session:', error);
        setError({
          message: 'Failed to initialize RTTY session',
          details: error.message || error.toString(),
          stack: error.stack
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, [serial]);


  // Helper function to cancel transfer
  const cancelTransfer = useCallback((errorMessage = null) => {
    // Mark upload as canceled to stop chunk sending
    uploadCanceledRef.current = true;
    
    // Send Ctrl+C multiple times to ensure it's received (sometimes needs to be sent twice)
    if (sendCommandRef.current) {
      console.log('[Cancel] Sending Ctrl+C to interrupt transfer');
      // Ctrl+C is character code 3, send it as raw input
      sendCommandRef.current('\x03', true); // true = raw mode (no newline)
      // Send again after a short delay to ensure it's processed
      setTimeout(() => {
        if (sendCommandRef.current) {
          sendCommandRef.current('\x03', true);
        }
      }, 100);
    }
    
    // Don't immediately reset transferringRef - keep it true briefly to allow cancel confirmation
    // Reset state after a short delay to allow the cancel to be processed
    setTimeout(() => {
      setTransferring(false);
      transferringRef.current = false;
      setSuppressTerminal(false);
      if (errorMessage) {
        setTransferStatus(errorMessage);
      } else {
        setTransferStatus('Transfer canceled');
      }
      setTransferProgress(0);
      downloadBufferRef.current = [];
      downloadFilenameRef.current = null;
      downloadTotalSizeRef.current = 0;
      uploadCanceledRef.current = false; // Reset cancellation flag
    }, 200);
  }, []);

  const handleError = useCallback((error) => {
    console.error('RTTY error:', error);
    setError({
      message: typeof error === 'string' ? error : 'Terminal connection error',
      details: error.message || error.toString(),
      stack: error.stack
    });
  }, []);

  const handleConnectionChange = useCallback((connected) => {
    setIsConnected(connected);
    if (!connected) {
      sendCommandRef.current = null;
    }
  }, []);

  const handleTerminalReady = useCallback((sendCommand, terminalInstance) => {
    sendCommandRef.current = sendCommand;
    terminalRef.current = terminalInstance;
  }, []);

  const handleClose = () => {
    try {
      window.close();
    } catch (e) {
      navigate(`/device/${serial}`);
    }
  };

  // File Upload Handlers
  const handleUploadClick = () => {
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setDeviceFilePath('/tmp/');
    setTransferProgress(0);
    setTransferStatus('');
    setTransferring(false);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Default to /tmp/ + filename
      setDeviceFilePath(`/tmp/${file.name}`);
    }
  };

  const performUpload = useCallback(async (file, filepath, sessionId, targetName) => {
    try {
      // Reset cancellation flag for new upload
      uploadCanceledRef.current = false;
      
      // Initiate upload
      await api.startFileUpload(serial, {
        session_id: sessionId,
        filename: targetName
      });

      // Read file and send in chunks
      const chunkSize = 8192; // 8KB chunks
      const fileSize = file.size;
      let offset = 0;

      const reader = new FileReader();

      const sendNextChunk = () => {
        // Check if upload was canceled
        if (uploadCanceledRef.current) {
          console.log('[Upload] Canceled - stopping chunk sending');
          return;
        }
        
        if (offset >= fileSize) {
          // Send final empty DATA message to signal completion
          (async () => {
            // Check again before sending final chunk
            if (uploadCanceledRef.current) {
              console.log('[Upload] Canceled - skipping final chunk');
              return;
            }
            try {
              await api.sendFileChunk(serial, {
                session_id: sessionId,
                data: [], // empty chunk signals end
                file_size: fileSize,
                filename: file.name
              });
              console.log('Upload complete! (final empty chunk sent)');
              setTransferProgress(100);
              setTransferStatus('Upload complete');
              setTransferring(false);
              setSuppressTerminal(false);
              setUploadDialogOpen(false);
            } catch (e) {
              console.error('Failed to send final empty chunk:', e);
              setTransferStatus('Upload finished but finalization failed');
              setTransferring(false);
              setSuppressTerminal(false);
            }
          })();
          return;
        }

        const slice = file.slice(offset, offset + chunkSize);
        reader.readAsArrayBuffer(slice);
      };

      reader.onload = async (e) => {
        // Check if upload was canceled before processing this chunk
        if (uploadCanceledRef.current) {
          console.log('[Upload] Canceled - discarding chunk');
          return;
        }
        
        const chunk = new Uint8Array(e.target.result);
        
        try {
          await api.sendFileChunk(serial, {
            session_id: sessionId,
            data: Array.from(chunk),
            file_size: fileSize,
            filename: targetName
          });

          // Check again after sending (in case it was canceled during the async operation)
          if (uploadCanceledRef.current) {
            console.log('[Upload] Canceled after sending chunk - stopping');
            return;
          }

          offset += chunk.length;
          const progress = Math.round((offset / fileSize) * 100);
          console.log(`Upload progress: ${progress}% (${offset}/${fileSize} bytes)`);
          
          // Update progress bar and status
          setTransferProgress(progress);
          setTransferStatus(`${progress}%`);

          // Send next chunk
          sendNextChunk();
        } catch (error) {
          // If canceled, don't show error
          if (uploadCanceledRef.current) {
            console.log('[Upload] Canceled - ignoring error');
            return;
          }
          console.error('Failed to send chunk:', error);
          setTransferStatus('Upload failed');
          setTransferring(false);
          setSuppressTerminal(false);
        }
      };

      reader.onerror = () => {
        console.error('Failed to read file');
        setTransferStatus('Failed to read file');
        setTransferring(false);
        setSuppressTerminal(false);
      };

      sendNextChunk();

    } catch (error) {
      console.error('Upload error:', error);
      setTransferStatus('Upload failed');
      setTransferring(false);
      setSuppressTerminal(false);
    }
  }, [serial]);

  const handleUpload = async () => {
    if (!selectedFile || !deviceFilePath || !rttySession || !sendCommandRef.current) return;

    console.log('[Upload] Starting upload:', {
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      devicePath: deviceFilePath
    });

    // Keep dialog open and show progress; suppress terminal
    setTransferring(true);
    transferringRef.current = true;
    setTransferProgress(0);
    setTransferStatus('Preparing upload...');
    setSuppressTerminal(true);

    // Derive directory and target filename from deviceFilePath
    let targetDir = '/';
    let targetName = selectedFile.name;
    try {
      const lastSlash = deviceFilePath.lastIndexOf('/')
      if (lastSlash >= 0) {
        targetDir = deviceFilePath.slice(0, lastSlash) || '/';
        targetName = deviceFilePath.slice(lastSlash + 1) || selectedFile.name;
      } else {
        targetDir = '.';
        targetName = deviceFilePath;
      }
    } catch (e) {
      // fallback already set
    }

    // Delay command slightly so terminal suppression prop applies before echo
    setTimeout(() => {
      const command = `cd ${targetDir} && (sudo -n rtty -R || rtty -R)`;
      console.log('[Upload] Sending command:', command);
      const success = sendCommandRef.current(command);
      if (!success) {
        console.error('[Upload] Failed to send command');
        setTransferStatus('Failed to send command');
        setTransferring(false);
        transferringRef.current = false;
        setSuppressTerminal(false);
        return;
      }
      console.log('[Upload] Command sent successfully, waiting for device response...');
      // Wait 1 second for device to set up file transfer context, then start upload
      setTimeout(() => {
        console.log('[Upload] Starting file upload process');
        performUpload(selectedFile, deviceFilePath, rttySession.connection_id, targetName);
      }, 1000);
    }, 500);
  };

  // File Download Handlers
  const handleDownloadClick = () => {
    setDownloadDialogOpen(true);
    setDownloadPath('');
    setTransferProgress(0);
    setTransferStatus('');
    downloadBufferRef.current = [];
    downloadFilenameRef.current = null;
  };

  const handleDownload = async () => {
    if (!downloadPath || !sendCommandRef.current) return;
    console.log('[Download] Starting download for path:', downloadPath);
    
    // Keep dialog open and show progress
    setTransferring(true);
    transferringRef.current = true;
    setTransferProgress(0);
    setTransferStatus('Starting download...');
    setSuppressTerminal(true);
    downloadTotalSizeRef.current = 0; // Reset total size
    
    // Remember requested path for filename fallback later
    requestedDownloadPathRef.current = downloadPath;

    // Delay command slightly so terminal suppression prop applies before echo
    setTimeout(() => {
      const command = `(sudo -n rtty -S ${downloadPath} || rtty -S ${downloadPath})`;
      console.log('[Download] Sending command:', command);
      const success = sendCommandRef.current(command);
      if (!success) {
        console.error('[Download] Failed to send command');
        setTransferStatus('Failed to start download');
        setTransferring(false);
        transferringRef.current = false;
        setSuppressTerminal(false);
      } else {
        console.log('[Download] Command sent successfully, waiting for response...');
      }
    }, 500);
    
    setDownloadPath('');
  };

  const saveDownloadedFile = useCallback(() => {
    if (downloadBufferRef.current.length === 0) {
      console.log('No data to save');
      return;
    }

    // Calculate total size
    const totalSize = downloadBufferRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
    console.log(`Saving file: ${totalSize} bytes from ${downloadBufferRef.current.length} chunks`);
    
    // Combine all chunks into single array
    const fileData = new Uint8Array(totalSize);
    let offset = 0;
    for (const chunk of downloadBufferRef.current) {
      fileData.set(chunk, offset);
      offset += chunk.length;
    }

    // Create blob and trigger download
    const blob = new Blob([fileData], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Use the filename from the device message, or fall back to originally requested path
    const fallbackRequested = requestedDownloadPathRef.current
      ? requestedDownloadPathRef.current.split('/').pop()
      : '';
    a.download = downloadFilenameRef.current || fallbackRequested || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setTransferProgress(100);
    setTransferStatus(`Download complete! Saved ${totalSize} bytes`);
    setTransferring(false);

    // Clear buffer
    downloadBufferRef.current = [];
  }, []);

  const handleFileMessage = useCallback((fileMsg) => {
    console.log('[File Message] Received file message:', fileMsg);
    console.log('[File Message] Operation:', fileMsg.operation);
    console.log('[File Message] Full message:', JSON.stringify(fileMsg, null, 2));

    // Actual device-side operation codes (device_side_rtty_src/file.h):
    // 0 = SEND (device wants to send - download from device)
    // 1 = RECV (device ready to receive - upload to device)
    // 2 = INFO (file info)
    // 3 = DATA (file data chunks)
    // 4 = ACK, 5 = ABORT, 6 = BUSY, 7 = PROGRESS, etc.
    
    // For DOWNLOAD from device: Handle SEND (operation 0) - filename comes here
    if (fileMsg.operation === 0) { // FileOpSend - device wants to send
      if (fileMsg.filename) {
        console.log('Device wants to send file:', fileMsg.filename);
        downloadFilenameRef.current = fileMsg.filename;
        setTransferStatus('');
        downloadBufferRef.current = []; // Reset buffer for new download
      } else if (fileMsg.data) {
        // Filename might be in data field
        try {
          let filename;
          if (typeof fileMsg.data === 'string') {
            filename = fileMsg.data;
          } else if (Array.isArray(fileMsg.data)) {
            filename = String.fromCharCode.apply(null, fileMsg.data);
          } else {
            const bytes = new Uint8Array(fileMsg.data);
            filename = String.fromCharCode.apply(null, bytes);
          }
          console.log('Download filename from device:', filename);
          downloadFilenameRef.current = filename;
          setTransferStatus('');
          downloadBufferRef.current = []; // Reset buffer for new download
        } catch (error) {
          console.error('Failed to parse filename:', error);
        }
      }
      return;
    }
    
    // Note: The device does NOT send a "ready to receive" message for upload
    // It just accepts INFO messages directly after rtty -R is issued
    
    // For DOWNLOAD from device: INFO (operation 2) contains file data!
    if (fileMsg.operation === 2) { // FileOpInfo
      if (!fileMsg.info_data || (typeof fileMsg.info_data === 'string' && fileMsg.info_data.length === 0)) {
        // Empty INFO = transfer complete
        console.log('Received empty INFO message - transfer complete');
        if (downloadBufferRef.current.length > 0) {
          saveDownloadedFile();
        }
        setTransferProgress(100);
        setTransferStatus('Download complete');
        setTransferring(false);
        setSuppressTerminal(false);
        setDownloadDialogOpen(false);
        return;
      }
      
      // INFO contains file content (base64 encoded)
      try {
        let bytes;
        if (typeof fileMsg.info_data === 'string') {
          // Decode base64 string to bytes
          const binaryString = atob(fileMsg.info_data);
          bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
        } else if (Array.isArray(fileMsg.info_data)) {
          bytes = new Uint8Array(fileMsg.info_data);
        } else {
          bytes = new Uint8Array(fileMsg.info_data);
        }
        
        downloadBufferRef.current.push(bytes);
        const totalReceived = downloadBufferRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
        console.log(`[Download] Received file data in INFO: ${bytes.length} bytes (total: ${totalReceived} bytes, chunks: ${downloadBufferRef.current.length})`);
        
        // Calculate progress if we know the total size
        if (downloadTotalSizeRef.current > 0) {
          const progress = Math.round((totalReceived / downloadTotalSizeRef.current) * 100);
          setTransferProgress(Math.min(100, progress));
          setTransferStatus(`${progress}%`);
        } else {
          setTransferStatus(`${totalReceived} bytes`);
        }
      } catch (error) {
        console.error('Failed to process INFO data:', error);
      }
      return;
    }
    
    // For DOWNLOAD from device: File content comes in DATA messages (operation 3)
    if (fileMsg.operation === 3) { // FileOpData
      // Empty DATA message (len=0) signals transfer completion
      if (!fileMsg.data || (Array.isArray(fileMsg.data) && fileMsg.data.length === 0)) {
        console.log('Received empty DATA message - transfer complete');
        if (downloadBufferRef.current.length > 0) {
          saveDownloadedFile();
        }
        return;
      }
      
      // DATA message with file content
      try {
        let bytes;
        if (typeof fileMsg.data === 'string') {
          // Decode base64 string to bytes
          const binaryString = atob(fileMsg.data);
          bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
        } else if (Array.isArray(fileMsg.data)) {
          bytes = new Uint8Array(fileMsg.data);
        } else {
          bytes = new Uint8Array(fileMsg.data);
        }
        
        downloadBufferRef.current.push(bytes);
        const totalReceived = downloadBufferRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
        console.log(`[Download] Received file data: ${bytes.length} bytes (total: ${totalReceived} bytes, chunks: ${downloadBufferRef.current.length})`);
        
        // Calculate progress if we know the total size
        if (downloadTotalSizeRef.current > 0) {
          const progress = Math.round((totalReceived / downloadTotalSizeRef.current) * 100);
          setTransferProgress(Math.min(100, progress));
          setTransferStatus(`${progress}%`);
        } else {
          setTransferStatus(`${totalReceived} bytes`);
        }
      } catch (error) {
        console.error('Failed to process file data:', error);
      }
    } else if (fileMsg.operation === 7) { // FileOpProgress
      console.log('[Download] Progress update received', fileMsg);
      if (fileMsg.remaining_bytes !== undefined) {
        if (fileMsg.remaining_bytes === 0) {
          // Transfer complete!
          console.log('[Download] Progress shows 0 bytes remaining - transfer complete');
          if (downloadBufferRef.current.length > 0) {
            saveDownloadedFile();
          }
          setTransferProgress(100);
          setTransferStatus('Download complete');
          setTransferring(false);
          setSuppressTerminal(false);
          setDownloadDialogOpen(false);
        } else {
          if (fileMsg.total_bytes !== undefined && fileMsg.total_bytes > 0) {
            // Store total size for progress calculation
            downloadTotalSizeRef.current = fileMsg.total_bytes;
            const done = fileMsg.total_bytes - fileMsg.remaining_bytes;
            const pct = Math.max(0, Math.min(100, Math.round((done * 100) / fileMsg.total_bytes)));
            console.log(`[Download] Progress: ${pct}% (${done}/${fileMsg.total_bytes} bytes)`);
            setTransferProgress(pct);
            setTransferStatus(`${pct}%`);
          } else {
            console.log(`[Download] Progress: ${fileMsg.remaining_bytes} bytes remaining`);
          }
        }
      }
    } else if (fileMsg.operation === 5) { // FileOpAbort
      console.log('Transfer aborted');
      setTransferStatus('Transfer canceled');
      setTransferring(false);
      setSuppressTerminal(false);
      
      // Save whatever we have
      if (downloadBufferRef.current.length > 0) {
        saveDownloadedFile();
      }
    } else if (fileMsg.error) {
      setTransferStatus('Transfer failed: ' + fileMsg.error);
      setTransferring(false);
      setSuppressTerminal(false);
    }
  }, [saveDownloadedFile]);

  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleClose}
            sx={{ flexShrink: 0, minWidth: 'auto' }}
          >
            Close
          </Button>
          <Typography 
            variant="h6" 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flexShrink: 1
            }}
          >
            RTTY - {serial}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center', ml: 2 }}>
          <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            File Transfer:
          </Typography>
          <Tooltip title="Upload file to device">
            <span>
              <IconButton
                color="primary"
                onClick={handleUploadClick}
                disabled={!isConnected}
                size="small"
              >
                <UploadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download file from device">
            <span>
              <IconButton
                color="primary"
                onClick={handleDownloadClick}
                disabled={!isConnected}
                size="small"
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {error.message}
          </Typography>
          {error.details && (
            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              Details: {error.details}
            </Typography>
          )}
          {error.stack && (
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                bgcolor: 'rgba(0, 0, 0, 0.05)',
                p: 1,
                borderRadius: 1
              }}
            >
              Stack Trace:
              {error.stack}
            </Typography>
          )}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      ) : rttySession ? (
        <Paper 
          elevation={3} 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ flexGrow: 1, height: '100%' }}>
            <RttyTerminal
              rttySession={rttySession}
              deviceConnected={true}
              onError={handleError}
              onConnectionChange={handleConnectionChange}
              onFileMessage={handleFileMessage}
              onTerminalReady={handleTerminalReady}
              suppressTerminalOutput={suppressTerminal}
              onTerminalText={(data) => {
                // Monitor terminal output for error messages and progress during file transfer
                if (!transferringRef.current) return;
                
                // Handle progress updates (from suppressed text)
                if (typeof data === 'object' && data.type === 'progress') {
                  console.log('[RttyPage] Terminal progress update:', data.percentage + '%');
                  setTransferProgress(data.percentage);
                  setTransferStatus(`${data.percentage}%`);
                  return;
                }
                
                // Handle error messages (string text)
                const text = typeof data === 'string' ? data : '';
                if (!text) return;
                
                console.log('[RttyPage] Terminal text received during transfer:', text);
                
                // Check for file transfer errors
                const errorPatterns = [
                  /open.*failed.*no such file/i,
                  /failed:.*no such file/i,
                  /no such file/i,
                  /file not found/i,
                  /cannot find/i,
                  /does not exist/i,
                  /failed to open/i,
                ];
                
                const hasError = errorPatterns.some(pattern => pattern.test(text));
                if (hasError) {
                  console.warn('[RttyPage] File transfer error detected:', text);
                  
                  // Extract just the error message, removing shell prompts and other noise
                  let errorMsg;
                  
                  // First, try to extract the specific error pattern: "open '...' failed: No such file"
                  const openFailedMatch = text.match(/open\s+['"]([^'"]+)['"]\s+failed:\s*([^\n]+)/i);
                  if (openFailedMatch) {
                    errorMsg = `open '${openFailedMatch[1]}' failed: ${openFailedMatch[2].trim()}`;
                  } else {
                    // Fallback: extract any line that contains "failed" and "no such file"
                    const lines = text.split(/\r?\n/);
                    const errorLine = lines.find(line => {
                      const trimmed = line.trim();
                      return /failed/i.test(trimmed) && 
                             /no such file|file not found/i.test(trimmed) &&
                             !/^.*?@.*?:.*?#\s*$/.test(trimmed) && // Not a shell prompt
                             !/^.*?sudo:.*?not found.*?$/i.test(trimmed) && // Not sudo error
                             !/^.*?(-ash:|sh:).*?$/i.test(trimmed); // Not shell error
                    });
                    
                    if (errorLine) {
                      // Clean up the line: remove any leading shell prompt patterns
                      errorMsg = errorLine
                        .replace(/^.*?@.*?:.*?#\s*/, '')
                        .replace(/^.*?sudo:.*?not found.*?$/i, '')
                        .replace(/^.*?(-ash:|sh:).*?$/i, '')
                        .trim();
                    } else {
                      // Last resort: use a generic message
                      errorMsg = 'File transfer failed: File not found';
                    }
                  }
                  
                  console.log('[RttyPage] Extracted error message:', errorMsg);
                  cancelTransfer(errorMsg);
                }
              }}
              fullscreen
            />
          </Box>
        </Paper>
      ) : null}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => {
        if (transferring) {
          if (window.confirm('Upload in progress. Cancel and close?')) {
            cancelTransfer();
            setUploadDialogOpen(false);
          }
        } else {
          setUploadDialogOpen(false);
        }
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload File to Device</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={transferring}
            >
              {selectedFile ? selectedFile.name : 'Select File'}
            </Button>
            
            <TextField
              label="Device Path"
              value={deviceFilePath}
              onChange={(e) => setDeviceFilePath(e.target.value)}
              disabled={transferring}
              placeholder="/tmp/filename"
              fullWidth
              helperText="Enter the full path where the file should be saved on the device"
            />

            {transferring && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {transferStatus}
                </Typography>
                {transferProgress > 0 ? (
                  <LinearProgress variant="determinate" value={transferProgress} />
                ) : (
                  <LinearProgress variant="indeterminate" />
                )}
              </Box>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (transferring) {
              if (window.confirm('Upload in progress. Cancel and close?')) {
                cancelTransfer();
                setUploadDialogOpen(false);
              }
            } else {
              setUploadDialogOpen(false);
            }
          }}>
            {transferring ? 'Cancel Upload' : 'Cancel'}
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || !deviceFilePath || !isConnected}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={downloadDialogOpen} onClose={() => {
        if (transferring) {
          if (window.confirm('Download in progress. Cancel and close?')) {
            cancelTransfer();
            setDownloadDialogOpen(false);
          }
        } else {
          setDownloadDialogOpen(false);
        }
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Download File from Device</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Device File Path"
              value={downloadPath}
              onChange={(e) => setDownloadPath(e.target.value)}
              disabled={transferring}
              placeholder="/path/to/file"
              fullWidth
              helperText="Enter the full path of the file on the device"
              autoFocus
            />

            {(transferring || transferStatus) && (
              <Box>
                <Typography 
                  variant="body2" 
                  color={
                    transferStatus && (
                      transferStatus.includes('failed') || 
                      transferStatus.includes('error') || 
                      transferStatus.includes('timeout') || 
                      transferStatus.includes('not found') ||
                      transferStatus.includes('No such file')
                    )
                      ? 'error' 
                      : 'text.secondary'
                  } 
                  gutterBottom
                >
                  {transferStatus || 'Processing...'}
                </Typography>
                {transferring && (
                  transferProgress > 0 ? (
                    <LinearProgress variant="determinate" value={transferProgress} />
                  ) : (
                    <LinearProgress variant="indeterminate" />
                  )
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (transferring) {
              if (window.confirm('Download in progress. Cancel and close?')) {
                cancelTransfer();
                setDownloadDialogOpen(false);
              }
            } else {
              setDownloadDialogOpen(false);
            }
          }}>
            {transferring ? 'Cancel Download' : 'Cancel'}
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            disabled={!downloadPath || transferring}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RttyPage; 